import { BellRing, QrCode, Volume2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { lerConfig, gravarConfig } from "@/features/mesa-qr/api";

import BlocoRecolhivel from "./BlocoRecolhivel";
import { SONS_DISPONIVEIS, tocarChamado } from "@/features/mesa-qr/somChamado";

// Configurações → Sistema → Preferências: bloco do pedido pela mesa (QR Code).
// Cada mudança salva sozinha — são chaves independentes, sem botão Salvar.
function Chave({ ligado, onAlternar, id }) {
  return (
    <button
      type="button" role="switch" aria-checked={!!ligado} onClick={onAlternar} data-e2e={id}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${ligado ? "bg-primary" : "bg-gray-300 dark:bg-gray-600"}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${ligado ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

function Linha({ titulo, descricao, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-t first:border-t-0">
      <div className="min-w-0">
        <p className="font-medium text-sm">{titulo}</p>
        <p className="text-xs text-muted-foreground">{descricao}</p>
      </div>
      <div className="shrink-0 flex items-center gap-2">{children}</div>
    </div>
  );
}

export default function MesaQrPreferencias({ toast }) {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    lerConfig().then(({ data }) => setCfg(data)).catch(() => setCfg(null));
  }, []);

  const salvar = async (mudanca, aviso) => {
    const anterior = cfg;
    setCfg((c) => ({ ...c, ...mudanca }));
    try {
      const { data } = await gravarConfig(mudanca);
      setCfg(data);
      if (aviso) toast({ title: aviso });
    } catch (e) {
      setCfg(anterior);
      toast({ title: "Erro ao salvar", description: "Tente de novo.", variant: "destructive" });
    }
  };

  // O switch principal ("Ativar o pedido pela mesa") fica na linha do título,
  // visível com o bloco fechado (pedido do dono, 29/09/2026).
  const resumo = !cfg ? "Carregando…" : cfg.enabled
    ? `Ligado${cfg.pagamento_online ? " · pagamento na mesa ligado" : ""}${cfg.chamar_garcom ? " · chamar garçom ligado" : ""}`
    : "Desligado — os QR Codes não abrem o cardápio";
  return (
    <BlocoRecolhivel e2e="card-mesa-qr" icone={QrCode} titulo="Pedido pela mesa (QR Code)" resumo={resumo} carregando={!cfg}
      controle={cfg && <Chave id="sw-mesa-qr" ligado={cfg.enabled} onAlternar={() => salvar({ enabled: !cfg.enabled },
        !cfg.enabled ? "Pedido pela mesa ligado" : "Pedido pela mesa desligado")} />}>
      {!cfg ? <p className="text-sm text-muted-foreground">Carregando o pedido pela mesa...</p> : (<div className="space-y-1">
      <p className="text-sm text-muted-foreground pb-2">
        O cliente escaneia o código na mesa, pede pelo celular e o pedido vai direto para a
        cozinha. Os códigos são gerados em Mesas, no botão QR Codes. Ele sempre se identifica
        pelo CPF antes da primeira rodada — é o que põe o nome de quem pediu em cada rodada da
        comanda e libera o cashback. O switch da linha de cima liga e desliga o recurso (desligado,
        os QR Codes não abrem o cardápio).
      </p>

      <Linha
        titulo="Pedir também o WhatsApp de quem é novo"
        descricao="Só para o CPF que ainda não tem cadastro. Quem já é cliente entra pelo CPF e não é perguntado."
      >
        <Chave id="sw-mesa-telefone" ligado={cfg.exige_telefone}
          onAlternar={() => salvar({ exige_telefone: !cfg.exige_telefone })} />
      </Linha>

      <Linha
        titulo="Pagamento na mesa"
        descricao="O cliente paga o dele (ou de quem quiser) no PIX pelo celular e recebe o comprovante com o QR de saída; a equipe lê o QR e libera. Desligado, somem o pagar pelo celular, o QR de saída, o 'Ler QR de saída' e o 'dar saída' no caixa — o Cobrar por pessoa no Fechar mesa continua."
      >
        <Chave id="sw-mesa-pagamento" ligado={cfg.pagamento_online}
          onAlternar={() => salvar({ pagamento_online: !cfg.pagamento_online })} />
      </Linha>
      {cfg.enabled && cfg.pagamento_online && !cfg.pix_online_mesas ? (
        <p className="text-xs text-amber-600 dark:text-amber-400 -mt-1 pb-3" data-e2e="aviso-pix-mesa">
          O pagamento na mesa só funciona com o PIX Online ligado para Mesas. Configure o Mercado Pago
          (Sistema → Integração) e deixe a forma "PIX Online" ativa e visível em Mesas (Financeiro →
          Formas de Pagamento). Até lá, fica tudo como desligado.
        </p>
      ) : null}

      <Linha
        titulo="Tolerância depois do horário de fechamento"
        descricao="O QR respeita o horário da loja. Aqui você dá uma folga para a mesa que ainda está ocupada na virada."
      >
        <div className="relative w-28">
          <Input
            type="number" min={0} max={240} inputMode="numeric" value={cfg.tolerancia_minutos}
            onChange={(e) => setCfg((c) => ({ ...c, tolerancia_minutos: e.target.value }))}
            onBlur={(e) => salvar({ tolerancia_minutos: Number(e.target.value || 0) })}
            className="h-10 pr-10 text-right font-bold tabular-nums"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">min</span>
        </div>
      </Linha>

      <Linha
        titulo="Exigir caixa aberto"
        descricao="Ligue só se a loja abre caixa todo dia. Com o caixa fechado, o QR recusa pedidos."
      >
        <Chave id="sw-mesa-caixa" ligado={cfg.exige_caixa_aberto}
          onAlternar={() => salvar({ exige_caixa_aberto: !cfg.exige_caixa_aberto })} />
      </Linha>

      <div className="pt-4 mt-2 border-t">
        <div className="flex items-center gap-2 mb-1">
          <BellRing className="w-4 h-4 text-primary" />
          <h4 className="font-semibold text-sm">Chamar o garçom</h4>
        </div>
        <p className="text-xs text-muted-foreground mb-2">
          Botão na comanda do cliente. O aviso e o som aparecem em qualquer tela do painel até
          alguém tocar em Atendido.
        </p>

        <Linha titulo="Ativar o botão de chamar garçom" descricao="Some da comanda quando desligado.">
          <Chave id="sw-mesa-garcom" ligado={cfg.chamar_garcom}
            onAlternar={() => salvar({ chamar_garcom: !cfg.chamar_garcom })} />
        </Linha>

        <Linha titulo="Som do chamado" descricao="Toque para ouvir antes de escolher.">
          <select
            value={cfg.garcom_som}
            onChange={(e) => salvar({ garcom_som: e.target.value })}
            className="h-10 rounded-md border bg-background px-2 text-sm"
            data-e2e="mesa-garcom-som"
          >
            {SONS_DISPONIVEIS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <button
            type="button"
            onClick={() => tocarChamado({ nome: cfg.garcom_som, volume: cfg.garcom_volume })}
            className="h-10 w-10 rounded-md border flex items-center justify-center hover:bg-muted"
            title="Ouvir"
          >
            <Volume2 className="w-4 h-4" />
          </button>
        </Linha>

        <Linha titulo="Volume" descricao="Vale para o som do chamado no painel.">
          <input
            type="range" min={10} max={100} step={5} value={cfg.garcom_volume}
            onChange={(e) => setCfg((c) => ({ ...c, garcom_volume: Number(e.target.value) }))}
            onMouseUp={(e) => salvar({ garcom_volume: Number(e.target.value) })}
            onTouchEnd={(e) => salvar({ garcom_volume: Number(e.target.value) })}
            className="w-32 accent-primary"
          />
          <span className="text-xs font-bold tabular-nums w-8 text-right">{cfg.garcom_volume}</span>
        </Linha>

        <Linha titulo="Repetir o som a cada" descricao="Enquanto o chamado não for atendido.">
          <div className="relative w-28">
            <Input
              type="number" min={5} max={300} inputMode="numeric" value={cfg.garcom_intervalo}
              onChange={(e) => setCfg((c) => ({ ...c, garcom_intervalo: e.target.value }))}
              onBlur={(e) => salvar({ garcom_intervalo: Number(e.target.value || 20) })}
              className="h-10 pr-8 text-right font-bold tabular-nums"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">s</span>
          </div>
        </Linha>

        <Linha
          titulo="Avisar no WhatsApp se ninguém atender"
          descricao="Manda no número de controle quando o chamado fica parado. Rede de segurança para quem não fica com o painel aberto."
        >
          <Chave id="sw-mesa-garcom-zap" ligado={cfg.garcom_whatsapp}
            onAlternar={() => salvar({ garcom_whatsapp: !cfg.garcom_whatsapp })} />
        </Linha>
      </div>
      </div>)}
    </BlocoRecolhivel>
  );
}
